import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaSave,
  FaPlus,
  FaTrash,
  FaCode,
  FaFileAlt,
  // FaFolder,
  // FaFolderOpen
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import CourseService from "../../../services/courseService";
import BatchService from "../../../services/batchService";
import AdminNotesService from "../../../services/adminNotesService";
import CodeBlock from "../../../components/common/CodeBlock";
import "../../../styles/notes-admin.css";

const CreateTopic = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [existingTopics, setExistingTopics] = useState([]);

  // Content Type: 'folder' or 'topic'
  const [contentType, setContentType] = useState("topic");

  // Topic Form State
  const [topicData, setTopicData] = useState({
    title: "",
    course_id: "",
    batch_id: "",
    parent_id: "",
    description: "",
    is_published: 1,
  });

  // Blocks State (only for topics)
  const [blocks, setBlocks] = useState([]);
  const [currentBlock, setCurrentBlock] = useState({
    block_type: "explanation",
    title: "",
    content: "",
    code_content: "",
    language: "javascript",
  });
  const [showBlockForm, setShowBlockForm] = useState(false);

  // Exercises State (only for topics)
  const [exercises, setExercises] = useState([]);
  const [currentExercise, setCurrentExercise] = useState({
    question: "",
    solution: "",
    solution_language: "javascript",
    difficulty: "medium",
    show_solution: 0,
  });
  const [showExerciseForm, setShowExerciseForm] = useState(false);

  useEffect(() => {
    loadCourses();
    loadBatches();
  }, []);

  useEffect(() => {
    if (topicData.course_id) {
      loadExistingTopics(topicData.course_id);
    }
  }, [topicData.course_id]);

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAllCourses();
      let coursesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response && Array.isArray(response)) {
        coursesData = response;
      }
      setCourses(coursesData);
    } catch (error) {
      toast.error("Failed to load courses");
    }
  };

  const loadBatches = async () => {
    try {
      const response = await BatchService.getAllBatches();
      setBatches(response.data || []);
    } catch (error) {
      console.error("Failed to load batches:", error);
    }
  };

  const loadExistingTopics = async (courseId) => {
    try {
      // Only fetch folders for parent dropdown (content_type = 'folder')
      const response = await AdminNotesService.getTopics(
        courseId,
        null,
        "folder",
      );
      setExistingTopics(response.data || []);
    } catch (error) {
      console.error("Failed to load folders:", error);
    }
  };

  const handleTopicChange = (e) => {
    setTopicData({ ...topicData, [e.target.name]: e.target.value });
  };

  // Build folder tree for parent dropdown
  const buildFolderOptions = (topics, level = 0, parentId = null) => {
    let options = [];
    const folders = topics.filter(
      (t) => t.content_type === "folder" && t.parent_id === parentId,
    );

    folders.forEach((folder) => {
      options.push({
        id: folder.id,
        title: "—".repeat(level) + " " + folder.title,
        level: level,
      });
      const children = buildFolderOptions(topics, level + 1, folder.id);
      options = [...options, ...children];
    });

    return options;
  };

  const folderOptions = buildFolderOptions(existingTopics);

  // Block Functions
  const addBlock = () => {
    if (
      !currentBlock.title &&
      !currentBlock.content &&
      !currentBlock.code_content
    ) {
      toast.error("Please add title and content for the block");
      return;
    }

    setBlocks([...blocks, { ...currentBlock, id: Date.now() }]);
    setCurrentBlock({
      block_type: "explanation",
      title: "",
      content: "",
      code_content: "",
      language: "javascript",
    });
    setShowBlockForm(false);
    toast.success("Block added successfully");
  };

  const removeBlock = (index) => {
    const newBlocks = [...blocks];
    newBlocks.splice(index, 1);
    setBlocks(newBlocks);
  };

  // Exercise Functions
  const addExercise = () => {
    if (!currentExercise.question) {
      toast.error("Please enter the exercise question");
      return;
    }

    setExercises([...exercises, { ...currentExercise, id: Date.now() }]);
    setCurrentExercise({
      question: "",
      solution: "",
      solution_language: "javascript",
      difficulty: "medium",
      show_solution: 0,
    });
    setShowExerciseForm(false);
    toast.success("Exercise added successfully");
  };

  const removeExercise = (index) => {
    const newExercises = [...exercises];
    newExercises.splice(index, 1);
    setExercises(newExercises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!topicData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    if (!topicData.course_id) {
      toast.error("Please select a course");
      return;
    }

    setLoading(true);

    try {
      // Create folder or topic
      const response = await AdminNotesService.createTopic({
        title: topicData.title,
        course_id: topicData.course_id,
        batch_id: topicData.batch_id || null,
        parent_id: topicData.parent_id || null,
        content_type: contentType,
        description: topicData.description,
        is_published: topicData.is_published,
      });

      const itemId = response.data.id;

      // If it's a topic, add blocks and exercises
      if (contentType === "topic") {
        for (const block of blocks) {
          await AdminNotesService.addBlock({
            topic_id: itemId,
            block_type: block.block_type,
            title: block.title,
            content: block.content,
            code_content: block.code_content,
            language: block.language,
          });
        }

        for (const exercise of exercises) {
          await AdminNotesService.addExercise({
            topic_id: itemId,
            question: exercise.question,
            solution: exercise.solution,
            solution_language: exercise.solution_language,
            difficulty: exercise.difficulty,
            show_solution: exercise.show_solution ? 1 : 0,
          });
        }

        toast.success(
          `Topic "${topicData.title}" created successfully with ${blocks.length} blocks and ${exercises.length} exercises`,
        );
      } else {
        toast.success(`Folder "${topicData.title}" created successfully`);
      }

      navigate("/admin/notes");
    } catch (error) {
      toast.error("Failed to create content");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none p-0 mb-2"
              onClick={() => navigate("/admin/notes")}
            >
              <FaArrowLeft className="me-2" /> Back to Notes
            </Button>
            <h2 className="mb-0">Create New Content</h2>
            <p className="text-muted">
              Add folders or educational content for students
            </p>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              {/* Content Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">📝 Content Information</h5>
                </Card.Header>
                <Card.Body>
                  {/* Content Type Selection */}
                  <Form.Group className="mb-3">
                    <Form.Label>Content Type</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="radio"
                        label="📁 Folder"
                        name="content_type"
                        value="folder"
                        checked={contentType === "folder"}
                        onChange={() => setContentType("folder")}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        label="📘 Topic (Notes)"
                        name="content_type"
                        value="topic"
                        checked={contentType === "topic"}
                        onChange={() => setContentType("topic")}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Folders help organize topics. Topics contain actual notes,
                      code, and exercises.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={topicData.title}
                      onChange={handleTopicChange}
                      placeholder={
                        contentType === "folder"
                          ? "e.g., Module 1: JavaScript Basics"
                          : "e.g., Introduction to Arrays"
                      }
                      required
                    />
                  </Form.Group>

                  {contentType === "topic" && (
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        rows={3}
                        value={topicData.description}
                        onChange={handleTopicChange}
                        placeholder="Brief description of this topic..."
                      />
                    </Form.Group>
                  )}

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Course *</Form.Label>
                        <Form.Select
                          name="course_id"
                          value={topicData.course_id}
                          onChange={handleTopicChange}
                          required
                        >
                          <option value="">Select Course</option>
                          {courses.map((course) => (
                            <option key={course.id} value={course.id}>
                              {course.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Parent Folder</Form.Label>
                        <Form.Select
                          name="parent_id"
                          value={topicData.parent_id}
                          onChange={handleTopicChange}
                        >
                          <option value="">— Root Level —</option>
                          {folderOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.title}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Place this item inside a folder
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Batch (Optional)</Form.Label>
                        <Form.Select
                          name="batch_id"
                          value={topicData.batch_id}
                          onChange={handleTopicChange}
                        >
                          <option value="">All Batches</option>
                          {batches.map((batch) => (
                            <option key={batch.id} value={batch.id}>
                              {batch.name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Leave empty to make available to all batches
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <div>
                          <Form.Check
                            inline
                            type="radio"
                            label="Published"
                            name="is_published"
                            value={1}
                            checked={topicData.is_published === 1}
                            onChange={() =>
                              setTopicData({ ...topicData, is_published: 1 })
                            }
                          />
                          <Form.Check
                            inline
                            type="radio"
                            label="Draft"
                            name="is_published"
                            value={0}
                            checked={topicData.is_published === 0}
                            onChange={() =>
                              setTopicData({ ...topicData, is_published: 0 })
                            }
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Content Blocks - Only for Topics */}
              {contentType === "topic" && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📄 Content Blocks</h5>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setShowBlockForm(true)}
                    >
                      <FaPlus className="me-1" /> Add Block
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {blocks.length === 0 ? (
                      <Alert variant="info" className="text-center">
                        No content blocks added yet. Click "Add Block" to add
                        content.
                      </Alert>
                    ) : (
                      blocks.map((block, index) => (
                        <div key={block.id} className="notes-block-item mb-3">
                          <div className="notes-block-header">
                            <div>
                              {block.block_type === "explanation" && (
                                <Badge bg="info">
                                  <FaFileAlt className="me-1" /> Explanation
                                </Badge>
                              )}
                              {block.block_type === "code" && (
                                <Badge bg="dark">
                                  <FaCode className="me-1" /> Code Block
                                </Badge>
                              )}
                              <strong className="ms-2">{block.title}</strong>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeBlock(index)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                          <div className="notes-block-preview mt-2">
                            {block.block_type === "explanation" &&
                              block.content && (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: block.content.substring(0, 200),
                                  }}
                                />
                              )}
                            {block.block_type === "code" &&
                              block.code_content && (
                                <CodeBlock
                                  code={block.code_content}
                                  language={block.language}
                                />
                              )}
                          </div>
                        </div>
                      ))
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Exercises - Only for Topics */}
              {contentType === "topic" && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">✏️ Practice Exercises</h5>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => setShowExerciseForm(true)}
                    >
                      <FaPlus className="me-1" /> Add Exercise
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {exercises.length === 0 ? (
                      <Alert variant="info" className="text-center">
                        No exercises added yet. Click "Add Exercise" to add
                        practice questions.
                      </Alert>
                    ) : (
                      exercises.map((exercise, index) => (
                        <div
                          key={exercise.id}
                          className="notes-exercise-item mb-3"
                        >
                          <div className="notes-exercise-header">
                            <div>
                              <Badge
                                bg={
                                  exercise.difficulty === "easy"
                                    ? "success"
                                    : exercise.difficulty === "medium"
                                      ? "warning"
                                      : "danger"
                                }
                              >
                                {exercise.difficulty}
                              </Badge>
                              <strong className="ms-2">
                                {exercise.question.substring(0, 100)}...
                              </strong>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeExercise(index)}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                          {exercise.solution && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Solution available
                              </small>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>

            <Col lg={4}>
              {/* Summary Card */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-secondary text-white">
                  <h5 className="mb-0">📊 Summary</h5>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Content Type:</span>
                    <Badge
                      bg={contentType === "folder" ? "warning" : "primary"}
                    >
                      {contentType === "folder" ? "📁 Folder" : "📘 Topic"}
                    </Badge>
                  </div>
                  {contentType === "topic" && (
                    <>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Content Blocks:</span>
                        <Badge bg="success">{blocks.length}</Badge>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Exercises:</span>
                        <Badge bg="warning">{exercises.length}</Badge>
                      </div>
                    </>
                  )}
                  <hr />
                  <div className="d-flex justify-content-between">
                    <span>Status:</span>
                    <Badge
                      bg={topicData.is_published ? "success" : "secondary"}
                    >
                      {topicData.is_published ? "Published" : "Draft"}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>

              {/* Action Buttons */}
              <Card className="shadow-sm">
                <Card.Body>
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100 mb-2"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Creating...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Create {contentType === "folder" ? "Folder" : "Topic"}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    className="w-100"
                    onClick={() => navigate("/admin/notes")}
                  >
                    Cancel
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>

      {/* Add Block Modal */}
      <Modal
        show={showBlockForm}
        onHide={() => setShowBlockForm(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Content Block</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Block Type</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Explanation"
                name="block_type"
                value="explanation"
                checked={currentBlock.block_type === "explanation"}
                onChange={(e) =>
                  setCurrentBlock({
                    ...currentBlock,
                    block_type: e.target.value,
                  })
                }
              />
              <Form.Check
                inline
                type="radio"
                label="Code Block"
                name="block_type"
                value="code"
                checked={currentBlock.block_type === "code"}
                onChange={(e) =>
                  setCurrentBlock({
                    ...currentBlock,
                    block_type: e.target.value,
                  })
                }
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={currentBlock.title}
              onChange={(e) =>
                setCurrentBlock({ ...currentBlock, title: e.target.value })
              }
              placeholder="Block title"
            />
          </Form.Group>

          {currentBlock.block_type === "explanation" ? (
            <Form.Group className="mb-3">
              <Form.Label>Content (HTML supported)</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                value={currentBlock.content}
                onChange={(e) =>
                  setCurrentBlock({ ...currentBlock, content: e.target.value })
                }
                placeholder="Write your explanation here. Use HTML tags for formatting: &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;li&gt;, etc."
              />
              <Form.Text className="text-muted">
                You can use HTML tags for better formatting.
              </Form.Text>
            </Form.Group>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Language</Form.Label>
                <Form.Select
                  value={currentBlock.language}
                  onChange={(e) =>
                    setCurrentBlock({
                      ...currentBlock,
                      language: e.target.value,
                    })
                  }
                >
                  <option value="javascript">JavaScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="php">PHP</option>
                  <option value="html">HTML</option>
                  <option value="css">CSS</option>
                  <option value="sql">SQL</option>
                  <option value="bash">Bash</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Code</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={currentBlock.code_content}
                  onChange={(e) =>
                    setCurrentBlock({
                      ...currentBlock,
                      code_content: e.target.value,
                    })
                  }
                  placeholder="Paste your code here..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlockForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={addBlock}>
            Add Block
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Exercise Modal */}
      <Modal
        show={showExerciseForm}
        onHide={() => setShowExerciseForm(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Practice Exercise</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Difficulty Level</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="Easy"
                name="difficulty"
                value="easy"
                checked={currentExercise.difficulty === "easy"}
                onChange={(e) =>
                  setCurrentExercise({
                    ...currentExercise,
                    difficulty: e.target.value,
                  })
                }
              />
              <Form.Check
                inline
                type="radio"
                label="Medium"
                name="difficulty"
                value="medium"
                checked={currentExercise.difficulty === "medium"}
                onChange={(e) =>
                  setCurrentExercise({
                    ...currentExercise,
                    difficulty: e.target.value,
                  })
                }
              />
              <Form.Check
                inline
                type="radio"
                label="Hard"
                name="difficulty"
                value="hard"
                checked={currentExercise.difficulty === "hard"}
                onChange={(e) =>
                  setCurrentExercise({
                    ...currentExercise,
                    difficulty: e.target.value,
                  })
                }
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Question</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={currentExercise.question}
              onChange={(e) =>
                setCurrentExercise({
                  ...currentExercise,
                  question: e.target.value,
                })
              }
              placeholder="Describe the exercise question..."
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Solution</Form.Label>
            <Form.Select
              value={currentExercise.solution_language}
              onChange={(e) =>
                setCurrentExercise({
                  ...currentExercise,
                  solution_language: e.target.value,
                })
              }
              className="mb-2"
            >
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
              <option value="java">Java</option>
              <option value="php">PHP</option>
            </Form.Select>
            <Form.Control
              as="textarea"
              rows={6}
              value={currentExercise.solution}
              onChange={(e) =>
                setCurrentExercise({
                  ...currentExercise,
                  solution: e.target.value,
                })
              }
              placeholder="Write the solution code here..."
            />
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="checkbox"
              label="Show solution by default"
              checked={currentExercise.show_solution}
              onChange={(e) =>
                setCurrentExercise({
                  ...currentExercise,
                  show_solution: e.target.checked,
                })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExerciseForm(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={addExercise}>
            Add Exercise
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreateTopic;
